import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

export const fetchDevices = createAsyncThunk('devices/fetchDevices', async ({ ownerId, ownerType, page = 1 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/devices/owner/${ownerType}/${ownerId}?page=${page}&limit=20`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchClientApps = createAsyncThunk('devices/fetchClientApps', async ({ ownerId, ownerType, page = 1 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/client-apps/owner/${ownerType}/${ownerId}?page=${page}&limit=20`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const createClientApp = createAsyncThunk('devices/createClientApp', async (appData, { rejectWithValue }) => {
  try {
    const response = await api.post('/client-apps', appData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const updateBranding = createAsyncThunk('devices/updateBranding', async ({ appId, branding }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/client-apps/${appId}/branding`, branding);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const requestBuild = createAsyncThunk('devices/requestBuild', async ({ appId, platform, version, buildType }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/client-apps/${appId}/build`, { platform, version, buildType });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchDeviceAnalytics = createAsyncThunk('devices/fetchAnalytics', async ({ tenantId, startDate, endDate }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const response = await api.get(`/device-analytics/summary/${tenantId}?${params}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchTopScreens = createAsyncThunk('devices/fetchTopScreens', async ({ tenantId, limit = 10 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/device-analytics/top-screens/${tenantId}?limit=${limit}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const trackEvent = createAsyncThunk('devices/trackEvent', async (eventData, { rejectWithValue }) => {
  try {
    await api.post('/device-analytics/track', eventData);
    return true;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

export const checkAppUpdate = createAsyncThunk('devices/checkUpdate', async ({ appId, platform, currentVersion }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/ota-updates/check/${appId}/${platform}?currentVersion=${currentVersion}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data);
  }
});

const devicesSlice = createSlice({
  name: 'devices',
  initialState: {
    devices: [],
    clientApps: [],
    currentApp: null,
    analytics: null,
    topScreens: [],
    pagination: { page: 1, pages: 1, total: 0 },
    appPagination: { page: 1, pages: 1, total: 0 },
    isLoading: false,
    error: null,
    successMessage: null
  },
  reducers: {
    clearDevicesError: (state) => { state.error = null; },
    clearDevicesSuccess: (state) => { state.successMessage = null; },
    setCurrentApp: (state, action) => { state.currentApp = action.payload; },
    resetDevicesState: (state) => {
      state.devices = [];
      state.clientApps = [];
      state.currentApp = null;
      state.analytics = null;
      state.topScreens = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDevices.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.devices = action.payload.devices || [];
        state.pagination = { page: action.payload.page, pages: action.payload.pages, total: action.payload.total };
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to load devices';
      })
      .addCase(fetchClientApps.pending, (state) => { state.isLoading = true; })
      .addCase(fetchClientApps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clientApps = action.payload.apps || [];
        state.appPagination = { page: action.payload.page, pages: action.payload.pages, total: action.payload.total };
      })
      .addCase(createClientApp.fulfilled, (state, action) => {
        state.clientApps.unshift(action.payload);
        state.successMessage = 'App created successfully';
      })
      .addCase(updateBranding.fulfilled, (state, action) => {
        state.currentApp = action.payload;
        const idx = state.clientApps.findIndex(a => a._id === action.payload._id);
        if (idx !== -1) state.clientApps[idx] = action.payload;
        state.successMessage = 'Branding updated';
      })
      .addCase(requestBuild.fulfilled, (state, action) => {
        state.successMessage = `Build requested: ${action.payload.platform} v${action.payload.version}`;
      })
      .addCase(fetchDeviceAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
      })
      .addCase(fetchTopScreens.fulfilled, (state, action) => {
        state.topScreens = action.payload;
      });
  }
});

export const { clearDevicesError, clearDevicesSuccess, setCurrentApp, resetDevicesState } = devicesSlice.actions;
export default devicesSlice.reducer;